import {
  Component,
  AfterViewInit,
  ViewEncapsulation,
  OnDestroy,
  HostListener,
  ViewChild,
  Renderer2,
  AfterContentChecked,
  ChangeDetectorRef,
  Injector,
} from '@angular/core';
// Type-only: SignalRService is resolved lazily in ensureSignalRService(), which runs only
// once a token exists. A value import kept @microsoft/signalr and its msgpack protocol in
// the eager vendor bundle for every visitor, and injecting the service ran
// new HubConnectionBuilder()...build() in its constructor — work that is pointless without
// a connection. `import type` is erased at build (no runtime cost).
import type { SignalRService } from './core/shared-services/signal-r.service';
import { Conversation, UpdateUgcVideoProgressModel } from './core/shared-models/signal-r';
import { CommonService } from './core/shared-services/common.service';
import { ActivatedRoute, NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router, RouterOutlet } from '@angular/router';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { ContactSupportService } from './shared/components/minimizable-windows/contact-support/contact-support.service';
import { PopupWindowService } from './core/shared-services/popup-window.service';
import { GoogleTagManagerConstants, LoaderKeys, LocalStorageKeys } from './core/constants/constants';
import { AppBootstrapService } from './core/shared-services/app-bootstrap.service';
import { filter, firstValueFrom, map } from 'rxjs';
import { sendGoogleTagManagerEvent } from './core/helpers/helper';
import { LocalStorageService } from './core/shared-services/local-storage.service';
import { LoaderService } from './core/shared-services/loader.service';
// IntegrationsService + its Integration_pb / BusinessIntegration_pb are no longer imported
// here: the 3 boot RPCs go through the lean Common aggregate (AppBootstrapService), and the
// service is resolved lazily (getIntegrationsService) only to populate `integratedApps` state.
// AdsService is likewise lazy-loaded inside getFacebookAdAccountStatus / getMetaIntegrationStatus
// (keeps BusinessAdCampaign_pb ~1.4MB out of the eager bundle).
import { ToasterService } from './core/shared-services/toaster.service';
// Lazy-loaded via dynamic import in ensureUgcToastService() so the toast and its
// UgcProgressDialog dependency stay OUT of the eager bundle. It's only pulled the
// first time a UGC video actually starts generating (a SignalR event) — never on
// the guest/landing path. `import type` is erased at build (no runtime cost).
import type { UgcGenerationToastService } from './shared/components/ugc-generation-toast/ugc-generation-toast.service';
// Constants file is a lightweight leaf (no heavy imports) so it stays static.
import { ToastMessages, getToastMessages, ToastMessagesAiVideo } from './shared/components/ugc-generation-toast/ugc-generation-toast.constants';
// Type-only: AiVideoService is resolved lazily in ensureAiVideoService(), which runs only when
// the browser has a pending AI Video generation to reconcile. A value import would pull the
// large AiVideoCreative_pb into the eager bundle. The store below is a pure-TS leaf — no cost.
import type { AiVideoService } from './modules/main/ai-video/ai-video.service';
import { AiVideoPendingStore } from './modules/main/ai-video/ai-video-pending-generations.store';
import { MADE_FOR_YOU_VIDEO_IDS } from './modules/main/inspirations/made-for-you/made-for-you-video-ids';
import { CommonModule, NgClass } from '@angular/common';
// ngx-capture (and its heavy html2canvas dep) is @defer-loaded via this standalone
// wrapper, so it stays out of the eager bundle until a screenshot is triggered.
import { SupportCaptureComponent } from './shared/components/support-capture/support-capture.component';
import { WebsiteScrapeService } from './core/shared-services/WebScrapingService';
import { VersionCheckService } from './core/shared-services/version-check.service';
import { TawkService } from './core/shared-services/tawk.service';
import { SessionTrackingService } from './core/shared-services/session-tracking.service';
import { IconsModule as TablerIconsModule } from './shared/design-system/icon/icon.module';
import { ShopifyEmbeddedService } from './core/shared-services/shopify-embedded.service';
import { AuthSessionService } from './core/shared-services/auth-session.service';

// CreationPhase (AiVideoCreative_pb): READY = 4, FAILED = 5. Inlined rather than imported so
// AiVideoCreative_pb stays out of the eager bundle — AiVideoService is lazy for the same reason.
const AI_VIDEO_PHASE_READY = 4;
const AI_VIDEO_PHASE_FAILED = 5;

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [CommonModule,
    RouterOutlet,
    NgClass,
    SupportCaptureComponent,
    TablerIconsModule,
    MatProgressSpinner,
  ],
})
export class AppComponent implements AfterViewInit, OnDestroy, AfterContentChecked {
  title = 'ClientApp';
  private readonly calendlyExpertConsultationUrl = 'https://calendly.com/xoopah/expert-consultation';
  isCalendlyPopupOpen = false;

  @ViewChild('screen', { static: true }) screen: any;
  private subscriber: any;
  isCaptureScreen: boolean = false;
  localStorageKeys = LocalStorageKeys;
  loaders = LoaderKeys;
  private scrapingNotificationSubscription: any;

  // Branded loader shown ONLY during the guest onboarding flow
  // (guest-creative-flow -> web-scrapper) so guests don't see a white blank
  // screen while those lazy chunks load. Not shown for any other route.
  isNavigating: boolean = false;

  // SignalR is resolved lazily, and only for a signed-in user. The hub is what powers the
  // UGC progress/status toasts; a guest never opens a connection, so registering listeners
  // for them achieved nothing while costing the whole library in the eager bundle.
  private signalRService?: SignalRService;
  // hubConnection.on() stacks handlers, so registering twice would fire every UGC
  // callback twice. startUgcSignalR() is called from more than one place by design.
  private ugcSignalRStarted = false;
  private async ensureSignalRService(): Promise<SignalRService> {
    if (!this.signalRService) {
      const { SignalRService } = await import('./core/shared-services/signal-r.service');
      this.signalRService = this.injector.get(SignalRService);
    }
    return this.signalRService;
  }

  /**
   * Loads SignalR, wires up the UGC progress/status listeners and opens the hub.
   *
   * The listeners used to be registered for everyone in ngOnInit, but they are inert
   * without a connection — and the connection has always been gated on a token — so a
   * guest was paying for the library and a built HubConnection to receive events that
   * could never arrive.
   *
   * Called from two places, because app.component's ngOnInit runs BEFORE the user logs in:
   * once from the signed-in branch of ngOnInit (page loaded with a token already present),
   * and again on navigation once a token appears (the user just logged in). Without the
   * second call the listeners were never attached for a session that started as a guest,
   * and the UGC progress toast sat frozen while the video generated.
   */
  private async startUgcSignalR(): Promise<void> {
    if (this.ugcSignalRStarted) {
      return;
    }
    this.ugcSignalRStarted = true;

    const signalRService = await this.ensureSignalRService();

    signalRService.updateUgcVideoProgressListener((updateUgcVideoProgressModel) => {
      this.handleUgcVideoProgress(updateUgcVideoProgressModel);
    });

    signalRService.updateCreditUsageListener((creditUsageModel) => {
      this.commonService.planCredits = this.commonService.planCredits - creditUsageModel.PlanCreditsUsed;
      this.commonService.addonCredits = this.commonService.addonCredits - creditUsageModel.AddonCreditsUsed;
    });

    // Listen for status updates to get file URLs when video is complete
    signalRService.updateUgcStatusListener(async (updateUgcStatusModel) => {
      const ugcCreativeId = updateUgcStatusModel.UgcCreativeId;
      const ugcToast = await this.ensureUgcToastService();

      if (ugcToast.isShowing(ugcCreativeId)) {
        // Toast still open - update it to completed
        ugcToast.updateProgress(
          ugcCreativeId,
          100,
          ToastMessages.completed,
          updateUgcStatusModel.FileUrl,
          updateUgcStatusModel.ThumbnailUrl,
          updateUgcStatusModel.GoalId
        );
      } else {
        // User had closed the progress toast - show completed toast so they see "Your video is ready!" and preview button
        const toastMessages = getToastMessages(updateUgcStatusModel.WithAvatar ?? true);
        ugcToast.show({
          ugcCreativeId,
          progress: 100,
          message: toastMessages.completed,
          adCampaignGoalId: updateUgcStatusModel.GoalId,
          withAvatar: updateUgcStatusModel.WithAvatar ?? true
        });
        ugcToast.updateProgress(
          ugcCreativeId,
          100,
          toastMessages.completed,
          updateUgcStatusModel.FileUrl,
          updateUgcStatusModel.ThumbnailUrl,
          updateUgcStatusModel.GoalId
        );
      }
      // Notify progress dialog to update progress bar to 100 and thumbnail (only for avatar flow)
      if (updateUgcStatusModel.WithAvatar) {
        ugcToast.notifyProgressDialog(ugcCreativeId, 100, updateUgcStatusModel.ThumbnailUrl);
      }
    });

    // AI Video: only a terminal READY / FAILED is ever pushed (no progress events).
    // The toast for it is keyed by the NEGATED creative id (see the generate dialog).
    // A VideoUrl means it rendered; its absence means it failed.
    signalRService.updateAiVideoStatusListener(async (update) => {
      // "Made for you" announces its own video (dock + ready popup); a toast too would cover the dock
      if (MADE_FOR_YOU_VIDEO_IDS.has(update.AiVideoCreativeId)) {
        AiVideoPendingStore.remove(update.AiVideoCreativeId);
        return;
      }
      const ugcToast = await this.ensureUgcToastService();
      this.showAiVideoTerminalToast(
        update.AiVideoCreativeId,
        !!update.VideoUrl,
        update.VideoUrl,
        update.ThumbnailUrl,
        ugcToast,
      );
      AiVideoPendingStore.remove(update.AiVideoCreativeId);
    });

    signalRService.startConnection();

    // That push is unbuffered and sent exactly once — a tab that was reloading, or had the socket
    // down, when a generation finished never sees it. Reconcile this browser's own pending
    // generations against the server now (covers the refresh), and again whenever the tab is
    // brought back to the foreground (covers "left the tab while it rendered").
    void this.reconcileAiVideoToasts();
    document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  private readonly onVisibilityChange = (): void => {
    if (document.visibilityState === 'visible' && this.commonService.checkIfTokenExits()) {
      void this.reconcileAiVideoToasts();
    }
  };

  /**
   * Raises (or completes) the bottom-right toast for a finished AI Video generation. Shared by
   * the live SignalR handler and the on-reconnect reconcile so both paths look identical.
   */
  private showAiVideoTerminalToast(
    creativeId: number,
    succeeded: boolean,
    videoUrl: string | undefined,
    thumbnailUrl: string | undefined,
    ugcToast: UgcGenerationToastService,
    title?: string,
  ): void {
    const key = -creativeId;
    const message = succeeded ? ToastMessagesAiVideo.completed : ToastMessagesAiVideo.failed;
    // Dismissed, or never shown because the page reloaded while it rendered — bring up a fresh
    // one already in the completed state so the user still gets the outcome and the handoff.
    if (!ugcToast.isShowing(key)) {
      ugcToast.show({ ugcCreativeId: key, aiVideo: true, progress: 100, message, title });
    }

    if (succeeded) {
      ugcToast.updateProgress(key, 100, message, videoUrl, thumbnailUrl);
    } else {
      ugcToast.updateProgress(key, 100, message);
    }
  }

  /**
   * Catches up on AI Video generations this browser submitted but never saw finish — the
   * completion push is fire-and-forget, so one sent mid-refresh is lost. A no-op with no network
   * call when nothing is pending; guarded so overlapping triggers issue one query at a time.
   */
  private reconcileInFlight = false;
  private async reconcileAiVideoToasts(): Promise<void> {
    if (this.reconcileInFlight) return;

    const pending = AiVideoPendingStore.read();
    if (!pending.length) return;

    this.reconcileInFlight = true;
    try {
      await this.runAiVideoReconcile(pending);
    } finally {
      this.reconcileInFlight = false;
    }
  }

  private async runAiVideoReconcile(pending: ReturnType<typeof AiVideoPendingStore.read>): Promise<void> {
    const [ugcToast, aiVideoService] = await Promise.all([
      this.ensureUgcToastService(),
      this.ensureAiVideoService(),
    ]);

    // Terminal phases only — a still-generating entry stays pending for the next trigger.
    const result = await aiVideoService
      .getMyCreations([AI_VIDEO_PHASE_READY, AI_VIDEO_PHASE_FAILED], 1, 100)
      .catch(() => null);
    if (!result) return; // leave every entry pending, try again on the next trigger

    const finishedById = new Map(result.creations.map((creation) => [creation.creativeId, creation]));

    for (const entry of pending) {
      const creation = finishedById.get(entry.creativeId);
      if (!creation) continue; // still generating, or aged out — read() already drops the old ones

      this.showAiVideoTerminalToast(
        creation.creativeId,
        !!creation.videoUrl,
        creation.videoUrl,
        creation.thumbnailUrl,
        ugcToast,
        creation.name || creation.templateName,
      );
      AiVideoPendingStore.remove(entry.creativeId);
    }
  }

  // AiVideoService is resolved lazily (dynamic import) so AiVideoCreative_pb stays out of the
  // eager main.js — reconcileAiVideoToasts only reaches here when a generation is pending.
  private aiVideoService?: AiVideoService;
  private async ensureAiVideoService(): Promise<AiVideoService> {

    if (!this.aiVideoService) {
      const { AiVideoService } = await import('./modules/main/ai-video/ai-video.service');
      this.aiVideoService = this.injector.get(AiVideoService);
    }
    return this.aiVideoService;
  }

  // UGC generation toast is resolved lazily on the first UGC progress/status event
  // so its chunk (toast component + UgcProgressDialog) is not in the eager bundle.
  private ugcToastService?: UgcGenerationToastService;
  private async ensureUgcToastService(): Promise<UgcGenerationToastService> {
    if (!this.ugcToastService) {
      const { UgcGenerationToastService } = await import('./shared/components/ugc-generation-toast/ugc-generation-toast.service');
      this.ugcToastService = this.injector.get(UgcGenerationToastService);
    }
    return this.ugcToastService;
  }

  // OnboardingService is resolved lazily (dynamic import) only on the logged-in boot
  // path, purely to populate its shared state for the lazy feature routes that read it.
  // This keeps its large BusinessOnboarding_pb out of the eager main.js — and guests
  // (no token) never reach this, so they never load it at all. The RPC itself now goes
  // through the lean AppBootstrapService (Common.proto).
  private async getOnboardingService() {
    const { OnboardingService } = await import('./core/shared-services/onboarding.service');
    return this.injector.get(OnboardingService);
  }

  // IntegrationsService is resolved lazily (dynamic import) only on the logged-in boot path,
  // to populate its shared `integratedApps` state for the lazy feature routes that read it.
  // Keeps Integration_pb / BusinessIntegration_pb / PaymentIntegration_pb out of the eager
  // main.js — the 3 boot RPCs themselves go through the lean AppBootstrapService (Common.proto).
  private async getIntegrationsService() {
    const { IntegrationsService } = await import('./modules/main/integrations/integrations.service');
    return this.injector.get(IntegrationsService);
  }

  constructor(
    private commonService: CommonService,
    public router: Router,
    public contactSupportService: ContactSupportService,
    private renderer: Renderer2,
    private popupWindowService: PopupWindowService,
    private cdr: ChangeDetectorRef,
    private route: ActivatedRoute,
    private appBootstrapService: AppBootstrapService,
    public localStorageService: LocalStorageService,
    private loaderService: LoaderService,
    private injector: Injector,
    private toasterService: ToasterService,
    private websiteScrapeService: WebsiteScrapeService,
    private versionCheckService: VersionCheckService,
    private tawkService: TawkService,
    private sessionTrackingService: SessionTrackingService,
    public shopifyEmbeddedService: ShopifyEmbeddedService,
    private authSessionService: AuthSessionService,
  ) { }
  ngAfterContentChecked(): void {

  }

  // The branded route loader is for the guest flow only:
  //  - /guest-creative-flow is always a guest entry point.
  //  - /web-scrapper is shared, so only show it for a guest (temporary account).
  private isGuestFlowNavigation(url: string): boolean {
    const path = (url || '').split('?')[0];
    if (path.startsWith('/guest-creative-flow')) {
      return true;
    }
    if (path.startsWith('/web-scrapper')) {
      return localStorage.getItem(LocalStorageKeys.isTemporaryAccount) === 'true';
    }
    return false;
  }

  ngOnInit() {
    // Deferred to idle: this is a background staleness check (it reloads the page when a new
    // build is deployed), but firing it during boot put a 0.3 KB request at the end of the
    // critical request chain — PageSpeed measured the chain's total latency as 2,503 ms with this
    // request as its last item. Running it after first paint takes it off that path entirely; a
    // second or two later is immaterial for detecting a redeploy.

    this.appBootstrapService.getIsCouponFeatureEnabled().then(res => {
      this.commonService.isCouponFeatureEnabled = res.iscouponfeatureenabled;
    });


    const checkVersion = () => this.versionCheckService.checkVersion();
    if ('requestIdleCallback' in window) {
      (window as any).requestIdleCallback(checkVersion, { timeout: 5000 });
    } else {
      setTimeout(checkVersion, 2000);
    }

    this.router.events.pipe(filter(e => e instanceof NavigationEnd)).subscribe((e) => {
      this.sessionTrackingService.updateLastActiveModule((e as NavigationEnd).urlAfterRedirects);

      // A session that starts as a guest has no token when ngOnInit runs, so the UGC
      // listeners cannot be attached there. Pick them up on the first navigation after a
      // token appears — i.e. straight after login. startUgcSignalR() guards itself, so
      // this is a no-op on every later navigation.
      if (this.commonService.checkIfTokenExits()) {
        void this.startUgcSignalR();
      }
    });

    // Show the branded loader ONLY for guest-flow navigations
    // (guest-creative-flow -> web-scrapper), not for any other route.
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.isNavigating = this.isGuestFlowNavigation(event.url);
      } else if (
        event instanceof NavigationEnd ||
        event instanceof NavigationCancel ||
        event instanceof NavigationError
      ) {
        this.isNavigating = false;
      }
    });

    const token = this.authSessionService.getToken() + '';

    // The public shared-creative page (/share/:code) is viewed by anyone, signed in or not, and must
    // not be redirected to a resumed guest route, onboarding or the scraper on load.
    const isSharedCreativePage = location.pathname.startsWith('/share/');

    // if (localStorage.getItem(LocalStorageKeys.isTemporaryAccount) === "true") {

    //for guest users show tawk widget without login, and show pre-chat form (if configured) to capture visitor details before starting the chat.
    if (location.href.includes('web-scrapper')) {
    }

    if (localStorage.getItem('lastGuestRoute') && !isSharedCreativePage) {
      if (location.href.includes('facebook-auth') || location.href.includes('google-auth')) { }
      else {
        let url = localStorage.getItem('lastGuestRoute');
        if (url) {
          if (!location.href.includes(url)) {
            this.router.navigateByUrl(url);
          }
        }
      }
    }

    // } else 
    if (location.href.includes('email-verification') || location.href.includes('reset-password')
      || location.href.includes('booking-confirmation-payment') || location.href.includes('google-auth')
      || location.href.includes('review-request-interim') || location.href.includes('download-invoice')
      || location.href.includes('signup') || location.href.includes('facebook-auth') || (location.href.includes('guest-creative-flow') && !this.commonService.checkIfTokenExits())
      || isSharedCreativePage) {
    } else {
      if (token != '' && token !== 'null') {
        this.appBootstrapService.checkTokenExpiry().then(async res => {
          if (res.isexpired) {
            // Dismiss all generating toasts on token expiry/logout
            this.ugcToastService?.dismiss();

            sendGoogleTagManagerEvent(
              [
                GoogleTagManagerConstants.CustomTriggerEvents.BusinessUserLogOutSuccessful
              ]
            )
            this.authSessionService.clearToken();
            localStorage.clear();
            this.router.navigate(['/login']);
            this.toasterService.showCustomSnackbar('Your session has expired. Please log in again.', 'error', 3000);
          } else {
            if (this.commonService.checkIfTokenExits()) {
              // Not awaited: loading SignalR must not hold up the boot settings call below.
              void this.startUgcSignalR();

              // These three RPCs share no data, but getBusinessOnboardingDetails used to sit inside
              // getBusinessSettings' continuation and getPackageStatus inside *its* continuation —
              // turning three independent calls into three sequential round-trips before the app
              // could settle. Issue them together; the handling order below is unchanged, since the
              // onboarding block still reads settings.businessid for its GTM event.
              const settingsPromise = this.appBootstrapService.getBusinessSettings();
              const onboardingPromise = this.appBootstrapService.getBusinessOnboardingDetails();
              const isCouponFeatureEnabledPromise = this.appBootstrapService.getIsCouponFeatureEnabled();

              // Independent of both handlers — apply as soon as it lands.
              this.appBootstrapService.getPackageStatus().then(res => {
                this.commonService.setPackageStatus(res);
                this.loaderService.stopLoading(this.loaders.GetPackageStatusKey)
              }).catch(() => { });

              // Holds a handled branch while the await below runs, so a rejection in the meantime
              // does not surface as an unhandled promise rejection.
              onboardingPromise.catch(() => { });

              let isCouponFeatureEnabled = await isCouponFeatureEnabledPromise;
              this.commonService.isCouponFeatureEnabled = isCouponFeatureEnabled.iscouponfeatureenabled;

              let settings = await settingsPromise;
              this.commonService.isArchiveNotificationEnabled = settings?.isarchivenotificationenabled ?? false;
              this.commonService.bookingLink = settings.bookingexternallink?.value + '';
              this.commonService.timezoneOffset = settings.timezoneoffset ? settings.timezoneoffset?.value : 0;
              this.commonService.isTestBusiness = settings.istestbusiness;
              this.commonService.isReferralUser = settings.isreferraluser;
              this.commonService.aiStaticCreativeGetStartedCount = settings?.aistaticcreativegetstartedcount;
              this.commonService.businessSettingsReady$.next();


              // const settingsResponse = await this.businessSettingService.getBusinessSettings();
              // this.businessSettingService.isOnboardingCompleted = settings?.isonboardingcompleted?.value;
              // sendGoogleTagManagerEvent(
              //   [GoogleTagManagerConstants.CustomTriggerEvents.MXPIdentifyUser],
              //   [
              //     [GoogleTagManagerConstants.DataLayerVars.BusinessId, settings.businessid]
              //   ]
              // )

              onboardingPromise.then(async res => {

                const onboardingService = await this.getOnboardingService();
                onboardingService.businessOnboardingDetails = res as any;
                onboardingService.businessOnboardingDetailsSubject$.next(res as any);
                onboardingService.isOnboardingCompleted = res ? res.isonboardingcompleted?.value : false;
                onboardingService.businessCategoryName = res?.businesscategoryname?.value;

                sendGoogleTagManagerEvent(
                  [
                    GoogleTagManagerConstants.CustomTriggerEvents.MXPIdentifyUser,
                    GoogleTagManagerConstants.CustomTriggerEvents.MXPRegisterSuperProperties
                  ],
                  [
                    [GoogleTagManagerConstants.DataLayerVars.BusinessId, settings.businessid],
                    [GoogleTagManagerConstants.DataLayerVars.BusinessName, res?.businessname?.value ?? ''],
                    [GoogleTagManagerConstants.DataLayerVars.BusinessEmail, localStorage.getItem(LocalStorageKeys.userEmail)],
                  ]
                )
                if (res && res?.isscrapingcompleted?.value == true) {
                  this.localStorageService.setItem(LocalStorageKeys.isScrapingCompleted, true);

                  if (res && res?.isonboardingcompleted?.value == true) {
                    this.localStorageService.setItem(LocalStorageKeys.IsOnboardingCompleted, true);
                  }
                  else {

                    if (!this.router.url.includes('connect-meta') && !this.router.url.includes('meta-ads')) {
                      this.router.navigate(['/onboard']);
                    }
                  }


                }
                else {
                  // Type-guarded: localStorageService.getItem JSON.parses with a '{}' fallback, so
                  // a missing userFirstName is an object, not a string — `|| ''` never fired and
                  // the router serialised it into the URL as "[object Object]".
                  const storedName = this.localStorageService.getItem(LocalStorageKeys.userFirstName);
                  let queryParams = { 'name': typeof storedName === 'string' ? storedName : '' };
                  this.router.navigate(['/web-scrapper'], { queryParams });
                }




              }).catch(() => { });

            }
            else {
              this.commonService.routeToLogin();
            }
          }

          //Check if user is a oms user user
          if (localStorage.getItem(LocalStorageKeys.IsOmsUser) === 'true') {
            this.commonService.isOmsUser = true;
          }

          // Extract token from URL and set it in localStorage in the constructor
          // this.route.queryParams.subscribe(async params => {
          //   const token = params[this.localStorageKeys.ExternalImpersonationToken];
          //   if (token) {
          //     //localStorage.removeItem('token');
          //     localStorage.setItem('token', token);
          //     localStorage.setItem(this.localStorageKeys.IsSuperAdmin, 'true');

          //     let getBusinessOnboardingDetailsRequest = new GetBusinessOnboardingDetailRequest();
          //     const onboardingDetailsResponse = await this.onboardingService.getBusinessOnboardingDetails(getBusinessOnboardingDetailsRequest);
          //     this.onboardingService.isOnboardingCompleted = onboardingDetailsResponse?.isonboardingcompleted?.value;
          //     this.removeTokenFromUrl();

          //     if (onboardingDetailsResponse?.isonboardingcompleted?.value) {
          //           this.localStorageService.setItem(LocalStorageKeys.IsOnboardingCompleted, true);
          //           this.router.navigate(['/attract']);
          //       } else {
          //         this.router.navigate(['/onboarding/details']);
          //       }
          //   }
          // });

          //Safer way to get rid of unexpected behaviour
          firstValueFrom(this.route.queryParams).then(async params => {
            const token = params[this.localStorageKeys.ExternalImpersonationToken];
            if (token) {
              localStorage.setItem('token', token);
              localStorage.setItem(this.localStorageKeys.IsSuperAdmin, 'true');

              const onboardingService = await this.getOnboardingService();
              onboardingService.isOnboardingCompleted = res ? res?.isonboardingcompleted?.value : false;

              this.removeTokenFromUrl();

              if (!this.router.url.includes('connect-meta') && !this.router.url.includes('meta-ads')) {

                if (res && res?.isonboardingcompleted?.value == true) {
                  this.localStorageService.setItem(this.localStorageKeys.IsOnboardingCompleted, true);
                  this.router.navigate(['/home']);
                } else {
                  this.router.navigate(['/onboard']);
                }
              }
            }
            // this.router.events.pipe(
            //   filter(event => event instanceof NavigationEnd),
            //   map(() => this.route),
            //   map(route => {
            //     while (route.firstChild) {
            //      route = route.firstChild;
            //     }
            //     return route;
            //    }),
            //    map(route => route.url)
            //   )
            //  .subscribe((data:any) => {})



            this.commonService.closeMinimizeWindow$.subscribe((res: any) => {
              this.isCaptureScreen = true;
            });
          });


        }).catch(() => {
          this.ugcToastService?.dismiss();
          this.authSessionService.clearToken();
          localStorage.clear();
          this.router.navigate(['/login']);

          this.toasterService.showCustomSnackbar('Your session has expired. Please log in again.', 'error', 3000);
        })
      }
    }


    if (this.commonService.checkIfTokenExits()) {
      if (localStorage.getItem(LocalStorageKeys.isTemporaryAccount) !== 'true') {
        this.getBusinessIntegrationSettings();
      }
    }

    this.subscribeToScrapingNotifications();
    this.commonService.openCalendlyPopupEvent$.subscribe(() => {
      this.openCalendlyPopup();
    });

  }

  get showGlobalCalendlyIcon(): boolean {
    const hasToken = this.authSessionService.hasToken();
    if (!hasToken) return false;
    if (this.isCalendlyPopupOpen) return false;

    const hiddenRoutes = [
      '/login',
      '/signup',
      '/reset-password',
      '/email-verification',
      '/google-auth',
      '/facebook-auth',
      '/booking-confirmation-payment',
      '/download-invoice',
      '/review-request-interim',
      '/guest-creative-flow'
    ];

    return !hiddenRoutes.some(route => this.router.url.includes(route));
  }

  openCalendlyPopup(): void {
    this.isCalendlyPopupOpen = true;
    this.renderer.addClass(document.body, 'calendly-open');
    setTimeout(() => this.initCalendlyInlineWidget(), 0);
  }

  closeCalendlyPopup(): void {
    this.isCalendlyPopupOpen = false;
    this.renderer.removeClass(document.body, 'calendly-open');
  }

  private initCalendlyInlineWidget(): void {
    const calendlyContainer = document.getElementById('calendly-embed');
    if (!calendlyContainer) return;

    calendlyContainer.innerHTML = '';

    const calendly = (window as Window & {
      Calendly?: {
        initInlineWidget: (options: {
          url: string;
          parentElement: HTMLElement;
          resize?: boolean;
        }) => void;
      };
    }).Calendly;

    if (calendly?.initInlineWidget) {
      calendly.initInlineWidget({
        url: this.calendlyExpertConsultationUrl,
        parentElement: calendlyContainer,
        resize: true
      });
      return;
    }

    const iframe = document.createElement('iframe');
    iframe.src = this.calendlyExpertConsultationUrl;
    iframe.width = '100%';
    iframe.height = '100%';
    iframe.style.border = '0';
    iframe.setAttribute('title', 'Calendly scheduling');
    calendlyContainer.appendChild(iframe);
  }

  private subscribeToScrapingNotifications(): void {

    this.scrapingNotificationSubscription = this.websiteScrapeService.notification$.subscribe(
      notification => {
        this.toasterService.showCustomSnackbar(
          notification.message,
          notification.type,
          3000
        );
      }
    );
  }

  async handleUgcVideoProgress(progressModel: UpdateUgcVideoProgressModel): Promise<void> {

    const progress = progressModel.Progress;
    const ugcCreativeId = progressModel.UgcCreativeId;
    const withAvatar = progressModel.WithAvatar !== undefined ? progressModel.WithAvatar : true;
    const ugcToast = await this.ensureUgcToastService();

    // Show toast when progress starts (check for specific ugcCreativeId)
    if (progress > 0 && progress < 100 && !ugcToast.isShowing(ugcCreativeId)) {
      // this.ugcGenerationToastService.show({
      //   message: ToastMessages.initial, // Initial message - will be auto-updated based on progress
      //   estimatedTime: ToastMessages.estimatedTime,
      //   showMenu: true,
      //   ugcCreativeId: ugcCreativeId,
      //   progress: progress,
      //   withAvatar: withAvatar
      // });
      // Notify progress dialog only when withAvatar is true (dialog only opens for avatar flow)
      if (withAvatar) {
        ugcToast.notifyProgressDialog(ugcCreativeId, progress, progressModel.ThumbnailUrl);
      }
    }
    // Update progress for specific toast (milestone jump from backend)
    // Don't pass message - let component's automatic message transition handle it
    else if (progress > 0 && progress < 100 && ugcToast.isShowing(ugcCreativeId)) {
      ugcToast.updateProgress(ugcCreativeId, progress);
      // Notify progress dialog only when withAvatar is true (dialog only opens for avatar flow)
      if (withAvatar) {
        ugcToast.notifyProgressDialog(ugcCreativeId, progress, progressModel.ThumbnailUrl);
      }
    }
    // When complete, update progress to 100 and show completed state (don't dismiss)
    else if (progress >= 100) {
      // Update progress using actual progress value (clamped to 100 max)
      // All messages come from frontend constants
      ugcToast.updateProgress(
        ugcCreativeId,
        Math.min(progress, 100),
        ToastMessages.completed
      );
      if (withAvatar) {
        ugcToast.notifyProgressDialog(ugcCreativeId, 100, progressModel.ThumbnailUrl);
      }
      this.toasterService.showCustomSnackbar('UGC ad video generated successfully!', 'success', 3000);
    }
  }


  @HostListener('window:beforeunload', ['$event'])
  unloadHandler(event: Event): void {
    this.sessionTrackingService.endSession();
    this.ngOnDestroy();
  }

  ngOnDestroy(): void {
    // Optional chaining: for a guest the service was never resolved, so there is nothing to stop.
    this.signalRService?.stop(false);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    this.renderer.removeClass(document.body, 'calendly-open');

    if (this.scrapingNotificationSubscription) {
      this.scrapingNotificationSubscription.unsubscribe();
    }
  }

  ngAfterViewInit() {
    this.cdr.detectChanges();
  }

  saveImage(image: any) {
    if (!image) {
      this.contactSupportService.isCaptureScreen = false;
    } else {
      this.contactSupportService.addScreenShot.next(image);
    }
  }

  removeTokenFromUrl() {
    // Remove token from the URL without causing a page reload
    const urlWithoutToken = this.router.url.split('?')[0];
    window.history.replaceState({}, document.title, urlWithoutToken);
    // window.location.reload();
  }


  getBusinessIntegrationSettings() {
    // RPC via the lean Common aggregate; IntegrationsService is resolved lazily only to
    // populate its shared `integratedApps` state — keeping Integration_pb / BusinessIntegration_pb
    // / PaymentIntegration_pb out of the eager main.js. Guests (no token) never reach this.
    this.appBootstrapService.getBusinessIntegrationSettings().then(async res => {
      const integrationsService = await this.getIntegrationsService();
      integrationsService.integratedApps.isAuthorizeConnected = res?.isauthorizeconnected ? res?.isauthorizeconnected : false;
      integrationsService.integratedApps.isFacebookConnected = res?.isfacebookconnected ? res?.isfacebookconnected : false;
      integrationsService.integratedApps.isGoogleConnected = res?.isgoogleconnected ? res?.isgoogleconnected : false;
      integrationsService.integratedApps.isInstagramConnected = res?.isinstagramconnected ? res?.isinstagramconnected : false;
      integrationsService.integratedApps.isStripeConnected = res?.isstripeconnected ? res?.isstripeconnected : false;
      integrationsService.integratedApps.isMetaMessagesSyncing = res?.ismetamessagessyncing ? res?.ismetamessagessyncing : false;
      integrationsService.integratedApps.facebookpagename = res?.facebookpagename?.value;
      integrationsService.integratedApps.isBusinessPortfolioConnected = res?.isbusinessportfolioconnected;
      integrationsService.integratedApps.iscustomaudiencetosaccepted = res?.iscustomaudiencetosaccepted;
      integrationsService.integratedApps.customaudiencetoslink = res?.customaudiencetoslink?.value;

      if (integrationsService.integratedApps.isFacebookConnected) {

        integrationsService.integratedApps.facebookPageName = res?.facebookpagename ? res?.facebookpagename.value : "";
        integrationsService.integratedApps.instagramUserName = res?.instagramusername ? res?.instagramusername.value : "";
        integrationsService.integratedApps.adAccountName = res?.adaccountname ? res?.adaccountname.value : "";

        integrationsService.integratedApps.adAccountId = res?.adaccountid?.value ? res?.adaccountid?.value : "";
        this.getFacebookAdAccountStatus();
        this.getMetaIntegrationStatus();
      }


      this.localStorageService.setItem('isMetaMessagesSyncing', integrationsService.integratedApps.isMetaMessagesSyncing);
      this.localStorageService.setItem('facebookPageName', integrationsService.integratedApps.facebookpagename);

      this.localStorageService.setItem(LocalStorageKeys.isBusinessPortfolioConnected, integrationsService.integratedApps.isBusinessPortfolioConnected);
      this.localStorageService.setItem(LocalStorageKeys.iscustomaudiencetosaccepted, integrationsService.integratedApps.iscustomaudiencetosaccepted);
      this.localStorageService.setItem(LocalStorageKeys.customaudiencetoslink, integrationsService.integratedApps.customaudiencetoslink);

    });
  }

  getFacebookAdAccountStatus() {
    this.appBootstrapService.getFacebookAdAccountStatus().then(async (res: any) => {
      // Lazy-load AdsService here so BusinessAdCampaign_pb (~1.4MB) is not in the eager bundle.
      const { AdsService } = await import('./modules/main/attract/ads.service');
      this.injector.get(AdsService).updateAccountStatus(res?.ispaymentmethodconnected, res?.accountstatus);

    });
  }

  //getmetaintegrationstatus

  getMetaIntegrationStatus() {
    this.appBootstrapService.getMetaIntegrationStatus().then(async (res: any) => {
      if (res.isactiveintegrationexist) {
        // Lazy-load AdsService (see getFacebookAdAccountStatus) to keep BusinessAdCampaign_pb out of the eager bundle.
        const { AdsService } = await import('./modules/main/attract/ads.service');
        this.injector.get(AdsService).updateMetaIntegrationStatus({ isAccessTokenValid: res.isaccesstokenvalid });
      }
    });
  }

}

